/**
 * What the order picking system agrees on, in one place.
 *
 * Two codebases read these values: this CMS, which builds the pick rounds ("waves"), and the
 * Expo app on the freezer scanners, which only follows them. The app cannot import from this
 * repo, so every value that crosses the boundary is also written into the wave document itself
 * (see `commands`) and into docs/wave-contract-v1.md. Change a value here and the contract doc
 * has to change with it.
 *
 * Background and decisions: planning/orderpicking-bouwplan-cms.md.
 */

/**
 * Version of the wave document the app reads. Inside version 1 fields may only be added; a
 * field that is removed or changes meaning is version 2, and the app refuses a version it does
 * not know rather than half-understanding it.
 */
export const WAVE_CONTRACT_VERSION = 1;

/** Orders per round, the number the client works with. Staff can compose a smaller one. */
export const WAVE_DEFAULT_SIZE = 20;

/**
 * Box sizes. The split into boxes is the SooCool colli split (api SooCoolColliService: 15 kg per
 * box, first-fit-decreasing on max(weight, volume)), because that split is what SooCool labels
 * and bills. KLEIN/GROOT is only a name put on each resulting box.
 *
 * BOX_CAPACITY_GRAM and BOX_TARE_GRAM must stay equal to `soocool.colli.max-gross-weight-gram`
 * and `soocool.colli.tare-gram` in api/src/main/resources/application-*.properties, or the box
 * advice on the packing slip and the labels SooCool prints will disagree.
 */
export const BOX_SMALL_MAX_GRAM = 7000;
export const BOX_CAPACITY_GRAM = 15000;
export const BOX_TARE_GRAM = 0;
export const BOX_SIZE = { SMALL: "KLEIN", LARGE: "GROOT" };

/**
 * A pick day is OPEN or RELEASED. Releasing is locking: once a day is released, the orders on it
 * are a frozen snapshot that waves are built from. There is no third "released but not locked"
 * state - nothing would ever read it.
 */
export const PICK_DAY_STATUS = { OPEN: "OPEN", RELEASED: "RELEASED" };

/**
 * Days between picking and delivery ("een dag ertussen"). 0 = pick on the delivery day, which is
 * how the warehouse works today. A setting rather than a rule baked into the date code, because
 * the client has not decided when the day in between starts.
 */
export const PICK_DAY_GAP_DAYS = 0;

export const WAVE_STATUS = { OPEN: "open", BUSY: "busy", DONE: "done", CANCELLED: "cancelled" };

/**
 * Picker report (functions/pickerStats): a gap of more than this between two scans on one scanner
 * does not count as picking time. Nobody picks for ten minutes without scanning; it is a break
 * taken without CMD-PAUSE, and counting it would let one lunch outweigh a week of picking.
 */
export const PICKER_IDLE_GAP_MS = 10 * 60 * 1000;

/** The longest period the picker report reads at once: it reads every event of every round. */
export const PICKER_REPORT_MAX_DAYS = 62;

/**
 * `orders/{id}.picking.status`. Deliberately separate from `orderStatus`: that field belongs to
 * the shipping exports (Trunkrs, OptimoRoute, SooCool set SHIPPED), and two systems writing one
 * field means the last one wins.
 */
export const PICKING_STATUS = {
  QUEUED: "queued",
  PICKING: "picking",
  PICKED: "picked",
  PARTIAL: "partial",
  ABORTED: "aborted",
};

/** What the app writes to `pickWaves/{waveId}/events`. The fold in functions/pickEvents reads them. */
export const PICK_EVENT_TYPES = {
  WAVE_STARTED: "wave_started",
  ORDER_STARTED: "order_started",
  LINE_PICKED: "line_picked",
  LINE_CONFIRMED_MANUALLY: "line_confirmed_manually",
  LOCATION_EMPTY: "location_empty",
  BOX_FULL: "box_full",
  BOX_OVERRIDE: "box_override",
  ORDER_COMPLETED: "order_completed",
  ORDER_ABORTED: "order_aborted",
  WAVE_PAUSED: "wave_paused",
  WAVE_RESUMED: "wave_resumed",
  WAVE_FINISHED: "wave_finished",
};

/**
 * Every scannable code starts with a prefix that says what it is, so the app can tell a vak from
 * a command from a badge without context. Order barcodes are the bare order number (what staff
 * already read on the slip) and SooCool colli barcodes are `{orderId}-{n}`; neither may ever be
 * matched as a prefix of the other.
 */
export const BARCODE_PREFIX = { LOCATION: "LOC-", COMMAND: "CMD-", BADGE: "BADGE-" };

/**
 * The command barcodes on the laminated card on the cart. Also copied into every wave document
 * (`commands`), so the app never hardcodes them and a renamed code needs no app release.
 */
export const COMMAND_CODES = {
  locationEmpty: "CMD-LOCATION-EMPTY",
  boxFull: "CMD-BOX-FULL",
  boxSmall: "CMD-BOX-KLEIN",
  boxLarge: "CMD-BOX-GROOT",
  confirmLine: "CMD-CONFIRM-LINE",
  abortOrder: "CMD-ORDER-ABORT",
  pause: "CMD-PAUSE",
};

/**
 * The vak labels: 8 per A4 on die-cut polyester laser sheets (the Avery L4775 layout, 99.1 × 67.7
 * mm), printed on the ordinary A4 laser printer in the office. Polyester because paper does not
 * survive the rime in the freezer. Everything the print needs is in this one object, so another
 * sheet - or a label printer - is a change here and nowhere else.
 *
 * A module (narrowest bar) of 0.5 mm is readable for the scanner (Urovo RT40, a long-range engine)
 * from well over arm's length, which is what "in het voorbijgaan scannen" needs.
 */
export const SHELF_LABEL_SHEET = {
  name: "A4, 8 etiketten van 99,1 × 67,7 mm",
  pageWidthMm: 210,
  pageHeightMm: 297,
  columns: 2,
  rows: 4,
  labelWidthMm: 99.1,
  labelHeightMm: 67.7,
  marginTopMm: 13.1,
  marginLeftMm: 4.65,
  gapXMm: 2.5,
  gapYMm: 0,
  barcodeModuleMm: 0.5,
  barcodeHeightMm: 16,
};

/**
 * Cards for the card holders on the baskets: 18 × 5 cm, with two cards side by side (2026-10-21;
 * the 39 × 70 mm holders of 2026-09-30 did not fit). On the left the vak card - code and barcode,
 * printed once - and on the right the product card - what lies there, no barcode, printed again
 * when a product moves. Plain paper, cut along the dashed lines; cards touch, so one cut
 * separates two. Twelve on an A4.
 *
 * 88 × 46 mm until the inside of the holder is measured: if it is smaller, change it here. 0.48 mm
 * per module: the longest code (LOC- and six characters) is 165 modules with its quiet zones,
 * 79 mm, inside 88 minus the padding. Wider bars than the old 0.38 read more easily from arm's
 * length.
 */
const HOLDER_CARD = {
  pageWidthMm: 210,
  pageHeightMm: 297,
  columns: 2,
  rows: 6,
  labelWidthMm: 88,
  labelHeightMm: 46,
  marginTopMm: 10.5,
  marginLeftMm: 17,
  gapXMm: 0,
  gapYMm: 0,
  paddingMm: 3,
  barcodeModuleMm: 0.48,
  barcodeHeightMm: 17,
  // Text sizes: the vak code, the small lines in the corners, and at most the product name (on two
  // lines) and its weight
  codeFontMm: 14,
  cornerFontMm: 2.6,
  nameMaxMm: 8,
  nameLines: 2,
  weightMaxMm: 14,
};

export const SHELF_CARD_SHEET = { ...HOLDER_CARD, name: "A4, 12 vakkaartjes van 88 × 46 mm", kind: "card" };
export const SHELF_PRODUCT_CARD_SHEET = {
  ...HOLDER_CARD,
  name: "A4, 12 productkaartjes van 88 × 46 mm",
  kind: "product",
};

/**
 * The same two cards, small, for the magnetic holders on the beams above the pallets of row 1
 * (2026-10-23): a holder takes one card, so the vak card and the product card go in two holders
 * side by side. The card does not slide in but is clamped between the holder's two rims, so it has
 * to be exactly as high as the holder: 40 mm, and the holder is 100 mm wide (Sam, measured after a
 * test print at 95 × 30 that fell out). 98 × 37 mm, fourteen on an A4, cut along the dashed lines.
 * The rims cover the top and bottom edge, so the content stays in the middle. The barcode keeps its
 * 0.48 mm module - the longest code is 79 mm, inside 98 minus the padding - and is only lower; the
 * product name gets one line.
 */
const MAGNET_CARD = {
  ...HOLDER_CARD,
  rows: 7,
  labelWidthMm: 98,
  labelHeightMm: 37,
  marginTopMm: 8.5,
  marginLeftMm: 7,
  paddingMm: 3,
  barcodeHeightMm: 15,
  codeFontMm: 12,
  cornerFontMm: 2.4,
  nameMaxMm: 8,
  nameLines: 1,
  weightMaxMm: 13,
};

export const SHELF_MAGNET_CARD_SHEET = { ...MAGNET_CARD, name: "A4, 14 vakkaartjes van 98 × 37 mm", kind: "card" };
export const SHELF_MAGNET_PRODUCT_SHEET = {
  ...MAGNET_CARD,
  name: "A4, 14 productkaartjes van 98 × 37 mm",
  kind: "product",
};

/** What "Vaketiketten printen" offers; cards first, they are what the warehouse uses. */
export const SHELF_LABEL_FORMATS = {
  card: SHELF_CARD_SHEET,
  product: SHELF_PRODUCT_CARD_SHEET,
  magnetCard: SHELF_MAGNET_CARD_SHEET,
  magnetProduct: SHELF_MAGNET_PRODUCT_SHEET,
  sticker: SHELF_LABEL_SHEET,
};

/**
 * Where the rows of the freezer stand, for the map on Vakken en looproute (2026-10-22). Left to
 * right as seen from above, with the door at the bottom: the U goes up gangpad 1 and down gangpad
 * 2. Walking back down gangpad 2, right is the middle block: row 4 ("gangpad 2 rechts") stands back
 * to back with row 2, and row 3 ("gangpad 2 links") along the outer wall (Sam). `numbering` says
 * where stelling 01 of a row is:
 * "up" starts at the door, "down" at the far end - a row is numbered in the direction it is
 * walked. The racks and baskets themselves come from the vak codes (101A is row 1, stelling 01,
 * vak A), so a stelling added later is on the map without a change here. Mirrors
 * scripts/vakken/vriezer.json, which the CMS build cannot read.
 */
export const FREEZER_MAP = {
  columns: [
    { row: 1, title: "Rij 1", subtitle: "Vloer", numbering: "up", letters: "front-back" },
    { aisle: "Gangpad 1", walking: "up" },
    { row: 2, title: "Rij 2", subtitle: "Manden", numbering: "up", letters: "top-down" },
    { row: 4, title: "Rij 4", subtitle: "Manden", numbering: "down", letters: "top-down" },
    { aisle: "Gangpad 2", walking: "down" },
    { row: 3, title: "Rij 3", subtitle: "Manden", numbering: "down", letters: "top-down" },
  ],
};
